import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  text?: string;
  fullScreen?: boolean;
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12'
};

const textSizeClasses = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg'
};

export const LoadingSpinner = ({ 
  size = 'md', 
  className,
  text,
  fullScreen = false
}: LoadingSpinnerProps) => {
  const content = (
    <div className={cn(
      'flex flex-col items-center justify-center gap-3',
      fullScreen && 'min-h-screen',
      className
    )}>
      <Loader2 
        className={cn(
          'animate-spin text-primary',
          sizeClasses[size]
        )} 
        aria-hidden="true"
      />
      {text && (
        <p className={cn(
          'text-muted-foreground animate-pulse',
          textSizeClasses[size]
        )}>
          {text}
        </p>
      )}
      <span className="sr-only">Loading...</span>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
        {content}
      </div>
    );
  }

  return content;
};

interface LoadingSkeletonProps {
  className?: string;
  lines?: number;
  animated?: boolean;
}

export const LoadingSkeleton = ({ 
  className, 
  lines = 1,
  animated = true
}: LoadingSkeletonProps) => {
  return (
    <div className={cn('space-y-3', className)} aria-busy="true" aria-live="polite">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className={cn(
            'h-4 bg-muted rounded-md',
            animated && 'animate-pulse',
            i === lines - 1 ? 'w-2/3' : i % 2 === 0 ? 'w-full' : 'w-5/6'
          )}
          style={{
            animationDelay: `${i * 100}ms`
          }}
        />
      ))}
      <span className="sr-only">Loading content...</span>
    </div>
  );
};

interface CardSkeletonProps {
  className?: string;
  showImage?: boolean;
  showActions?: boolean;
}

export const CardSkeleton = ({ 
  className,
  showImage = false,
  showActions = false
}: CardSkeletonProps) => {
  return (
    <div 
      className={cn(
        'rounded-xl border bg-card p-6 space-y-4',
        className
      )}
      aria-busy="true"
    >
      {showImage && (
        <div className="h-40 bg-muted rounded-lg animate-pulse" />
      )}
      <div className="space-y-3">
        <div className="h-5 bg-muted rounded w-3/4 animate-pulse" />
        <div className="h-4 bg-muted rounded w-full animate-pulse" style={{ animationDelay: '100ms' }} />
        <div className="h-4 bg-muted rounded w-5/6 animate-pulse" style={{ animationDelay: '200ms' }} />
      </div>
      {showActions && (
        <div className="flex gap-2 pt-2">
          <div className="h-9 bg-muted rounded-md w-20 animate-pulse" style={{ animationDelay: '300ms' }} />
          <div className="h-9 bg-muted rounded-md w-24 animate-pulse" style={{ animationDelay: '400ms' }} />
        </div>
      )}
      <span className="sr-only">Loading card...</span>
    </div>
  );
};

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export const TableSkeleton = ({ 
  rows = 5, 
  columns = 4,
  className 
}: TableSkeletonProps) => {
  return (
    <div className={cn('w-full', className)} aria-busy="true">
      <div className="flex gap-4 p-4 border-b">
        {Array.from({ length: columns }).map((_, i) => (
          <div
            key={`header-${i}`}
            className="h-4 bg-muted rounded flex-1 animate-pulse"
            style={{ animationDelay: `${i * 50}ms` }}
          />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={`row-${rowIndex}`} className="flex gap-4 p-4 border-b last:border-0">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <div
              key={`cell-${rowIndex}-${colIndex}`}
              className="h-4 bg-muted/70 rounded flex-1 animate-pulse"
              style={{ animationDelay: `${(rowIndex * columns + colIndex) * 30}ms` }}
            />
          ))}
        </div>
      ))}
      <span className="sr-only">Loading table...</span>
    </div>
  );
};

interface ChartSkeletonProps {
  type?: 'bar' | 'line' | 'pie';
  className?: string;
}

export const ChartSkeleton = ({ 
  type = 'bar',
  className 
}: ChartSkeletonProps) => {
  return (
    <div 
      className={cn(
        'relative h-64 bg-muted/30 rounded-lg overflow-hidden',
        className
      )}
      aria-busy="true"
    >
      {type === 'bar' && (
        <div className="absolute bottom-4 left-4 right-4 flex items-end gap-2 h-48">
          {[40, 65, 45, 80, 55, 70, 50].map((height, i) => (
            <div
              key={i}
              className="flex-1 bg-muted rounded-t animate-pulse"
              style={{ 
                height: `${height}%`,
                animationDelay: `${i * 100}ms`
              }}
            />
          ))}
        </div>
      )}
      {type === 'line' && (
        <div className="absolute inset-4 flex items-center">
          <div className="w-full h-1/2 border-b-2 border-muted animate-pulse rounded" />
        </div>
      )}
      {type === 'pie' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-32 h-32 rounded-full border-8 border-muted animate-pulse" />
        </div>
      )}
      <span className="sr-only">Loading chart...</span>
    </div>
  );
};

export default LoadingSpinner;